/**
 * Menu Planning Scheduler - Pure Functions
 * 
 * Client-side scheduling and validation utilities for menu planning.
 * Implements greedy list-scheduling algorithm with single active resource constraint.
 */

import { 
  Segment, 
  ScheduledSegment, 
  Conflict, 
  ValidationResult,
  SchedulingPriority 
} from '../types/menuPlanning';

/**
 * Schedule recipes using greedy list-scheduling algorithm
 * @param segments - Array of segments from multiple recipes
 * @param priority - Scheduling priority strategy
 * @returns Array of scheduled segments with start/end times
 */
export function scheduleRecipes(
  segments: Segment[],
  priority: SchedulingPriority = 'recipeOrder'
): ScheduledSegment[] {
  if (segments.length === 0) {
    return [];
  }

  // Group segments by recipe
  const recipeSegments: { [recipeId: number]: Segment[] } = {};
  for (const seg of segments) {
    if (!recipeSegments[seg.recipeId]) {
      recipeSegments[seg.recipeId] = [];
    }
    recipeSegments[seg.recipeId].push(seg);
  }

  // Sort segments within each recipe by step and segment index
  for (const recipeId in recipeSegments) {
    recipeSegments[recipeId].sort((a, b) => {
      const aIndex = (a.stepIndex || 0) * 1000 + a.segmentIndex;
      const bIndex = (b.stepIndex || 0) * 1000 + b.segmentIndex;
      return aIndex - bIndex;
    });
  }

  return greedySchedule(recipeSegments, priority);
}

/**
 * Greedy list-scheduling implementation
 */
function greedySchedule(
  recipeSegments: { [recipeId: number]: Segment[] },
  priority: SchedulingPriority
): ScheduledSegment[] {
  const scheduled: ScheduledSegment[] = [];
  const recipeQueues: { 
    [recipeId: string]: { segments: Segment[]; nextIndex: number } 
  } = {};

  // Initialize queues
  for (const [recipeId, segments] of Object.entries(recipeSegments)) {
    recipeQueues[recipeId] = {
      segments: [...segments],
      nextIndex: 0
    };
  }

  let currentTime = 0;
  let activeSegmentEndTime: number | null = null;

  while (true) {
    // Collect ready segments (next in queue for each recipe)
    const readyActive: Array<{ recipeId: string; segment: Segment; queue: any }> = [];
    const readyInactive: Array<{ recipeId: string; segment: Segment; queue: any }> = [];

    for (const [recipeId, queue] of Object.entries(recipeQueues)) {
      if (queue.nextIndex < queue.segments.length) {
        const segment = queue.segments[queue.nextIndex];
        if (segment.type === 'active') {
          readyActive.push({ recipeId, segment, queue });
        } else {
          readyInactive.push({ recipeId, segment, queue });
        }
      }
    }

    // If no ready segments, we're done
    if (readyActive.length === 0 && readyInactive.length === 0) {
      break;
    }

    // Start all ready inactive segments now (they don't block)
    for (const { segment, queue } of readyInactive) {
      const startSec = currentTime;
      const endSec = startSec + segment.durationSec;

      scheduled.push({
        ...segment,
        startSec,
        endSec
      });

      queue.nextIndex++;
    }

    // If no active segment running, start the next ready active
    if (activeSegmentEndTime === null || currentTime >= activeSegmentEndTime) {
      if (readyActive.length > 0) {
        // Apply priority
        let chosen;
        if (priority === 'shortestActiveFirst') {
          chosen = readyActive.reduce((min, curr) => 
            curr.segment.durationSec < min.segment.durationSec ? curr : min
          );
        } else {
          // recipeOrder: take first ready
          chosen = readyActive[0];
        }

        const { segment, queue } = chosen;
        const startSec = currentTime;
        const endSec = startSec + segment.durationSec;

        scheduled.push({
          ...segment,
          startSec,
          endSec
        });

        activeSegmentEndTime = endSec;
        queue.nextIndex++;
      }
    }

    // Advance time to next event
    const nextEvents: number[] = [];

    if (activeSegmentEndTime !== null && activeSegmentEndTime > currentTime) {
      nextEvents.push(activeSegmentEndTime);
    }

    // Find next segment that could become ready
    for (const queue of Object.values(recipeQueues)) {
      if (queue.nextIndex < queue.segments.length) {
        // Next segment becomes ready after current time
        nextEvents.push(currentTime + 1);
        break;
      }
    }

    if (nextEvents.length === 0) {
      break;
    }

    currentTime = Math.min(...nextEvents);
  }

  return scheduled;
}

/**
 * Validate a schedule for conflicts
 * @param scheduledSegments - Array of scheduled segments
 * @returns Validation result with any conflicts found
 */
export function validateSchedule(
  scheduledSegments: ScheduledSegment[]
): ValidationResult {
  const conflicts: Conflict[] = [];

  // Check for active/active overlaps
  const activeSegments = scheduledSegments.filter(s => s.type === 'active');

  for (let i = 0; i < activeSegments.length; i++) {
    for (let j = i + 1; j < activeSegments.length; j++) {
      const seg1 = activeSegments[i];
      const seg2 = activeSegments[j];

      const overlapSec = detectOverlap(seg1, seg2);

      if (overlapSec > 0) {
        conflicts.push({
          segment1: seg1,
          segment2: seg2,
          overlapSec
        });
      }
    }
  }

  // Check that segments within each recipe maintain order
  const segmentsByRecipe: { [recipeId: number]: ScheduledSegment[] } = {};
  for (const seg of scheduledSegments) {
    if (!segmentsByRecipe[seg.recipeId]) {
      segmentsByRecipe[seg.recipeId] = [];
    }
    segmentsByRecipe[seg.recipeId].push(seg);
  }

  for (const [recipeId, segments] of Object.entries(segmentsByRecipe)) {
    const sorted = [...segments].sort((a, b) => {
      const aIndex = (a.stepIndex || 0) * 1000 + a.segmentIndex;
      const bIndex = (b.stepIndex || 0) * 1000 + b.segmentIndex;
      return aIndex - bIndex;
    });

    // Check that end times respect order
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startSec < sorted[i - 1].endSec) {
        conflicts.push({
          segment1: sorted[i - 1],
          segment2: sorted[i],
          overlapSec: sorted[i - 1].endSec - sorted[i].startSec,
          type: 'order_violation',
          message: `Step order violated in recipe ${recipeId}`
        });
      }
    }
  }

  return {
    valid: conflicts.length === 0,
    conflicts
  };
}

/**
 * Detect overlap between two segments
 * @param seg1 - First segment
 * @param seg2 - Second segment
 * @returns Overlap duration in seconds (0 if no overlap)
 */
export function detectOverlap(
  seg1: ScheduledSegment,
  seg2: ScheduledSegment
): number {
  const overlapStart = Math.max(seg1.startSec, seg2.startSec);
  const overlapEnd = Math.min(seg1.endSec, seg2.endSec);
  return Math.max(0, overlapEnd - overlapStart);
}

/**
 * Format duration in seconds to human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted string like "1h 30m" or "45m"
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}

/**
 * Format seconds to time string
 * @param seconds - Time in seconds from start
 * @returns Formatted time string like "02:30" (HH:MM)
 */
export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}m`;
}

/**
 * Convert time string to seconds
 * @param timeStr - Time string like "1h 30m" or "45m"
 * @returns Duration in seconds
 */
export function parseTimeToSeconds(timeStr: string): number {
  let totalSeconds = 0;
  
  const hourMatch = timeStr.match(/(\d+)h/);
  const minuteMatch = timeStr.match(/(\d+)m/);
  const secondMatch = timeStr.match(/(\d+)s/);
  
  if (hourMatch) totalSeconds += parseInt(hourMatch[1]) * 3600;
  if (minuteMatch) totalSeconds += parseInt(minuteMatch[1]) * 60;
  if (secondMatch) totalSeconds += parseInt(secondMatch[1]);
  
  return totalSeconds;
}

/**
 * Calculate makespan (total time) for a schedule
 * @param scheduledSegments - Array of scheduled segments
 * @returns Maximum end time in seconds
 */
export function calculateMakespan(scheduledSegments: ScheduledSegment[]): number {
  if (scheduledSegments.length === 0) return 0;
  return Math.max(...scheduledSegments.map(s => s.endSec));
}

/**
 * Snap time to grid (in minutes)
 * @param seconds - Time in seconds
 * @param gridSizeMinutes - Grid size in minutes
 * @returns Snapped time in seconds
 */
export function snapToGrid(seconds: number, gridSizeMinutes: number = 1): number {
  const gridSizeSeconds = gridSizeMinutes * 60;
  return Math.round(seconds / gridSizeSeconds) * gridSizeSeconds;
}


